import { isEqual, isPresent, isProductionEnvironment, throwErrorUnlessProduction } from '@luminovo/commons';
import * as React from 'react';

export type StorageStateOptions<T> = {
    /** Where the state persists: `sessionStorage` (default) or `localStorage`. */
    storage?: Storage;
    /** Whether to persist the state; defaults to `true`. */
    enabled?: boolean;
    /** Whether a parsed value has the right type; an invalid one falls back to `initialValue`. */
    validation?: (value: unknown) => boolean;
    callback?: (value: T) => void;
};

export type SetStorageStateOptions = {
    /** Where the state persists: `sessionStorage` (default) or `localStorage`. */
    storage?: Storage;
    /** Whether to persist the state; defaults to `true`. */
    enabled?: boolean;
    /** Whether the JSON round-tripped value has the right type; an invalid one is removed again. */
    validation?: (value: unknown) => boolean;
};

/** `useState` persisted as JSON in storage, read once on mount; unlike `usePersistedState`, takes any JSON value. */
export function useStorageState<T>(
    storageKey: string,
    initialValue: T,
    options: StorageStateOptions<T> = { storage: sessionStorage, enabled: true, validation: () => true },
): [T, (newState: T | ((value: T) => T)) => void] {
    const { storage = sessionStorage, enabled = true, validation = () => true, callback } = options;

    const [state, setState] = React.useState(() =>
        readStorageState(storageKey, initialValue, { storage, enabled, validation }),
    );

    const handler = React.useCallback(
        (input: T | ((value: T) => T)) => {
            setState((oldState) => {
                const value = typeof input === 'function' ? (input as (value: T) => T)(oldState) : input;

                if (enabled) {
                    if (!isProductionEnvironment() && !isSerializable(value)) {
                        throwErrorUnlessProduction(
                            new Error(`Attempted to store non-serializable value under key "${storageKey}".`),
                            { extra: { value } },
                        );
                    }

                    if (value === undefined) {
                        storage.removeItem(storageKey);
                    } else {
                        storage.setItem(storageKey, JSON.stringify(value));
                    }
                }

                if (isPresent(callback)) {
                    callback(value);
                }

                return value;
            });
        },
        [storage, storageKey, enabled, setState, callback],
    );

    return [state, handler];
}

/** Reads a value as `useStorageState` does on mount: `initialValue` when disabled, missing or invalid. */
export function readStorageState<T>(
    storageKey: string,
    initialValue: T,
    { storage, enabled, validation }: { storage: Storage; enabled: boolean; validation: (value: unknown) => boolean },
): T {
    const item = storage.getItem(storageKey);
    if (!enabled || !isPresent(item)) {
        return initialValue;
    }

    try {
        const parsedItem = JSON.parse(item);
        if (!validation(parsedItem)) {
            throwErrorUnlessProduction(
                new Error(`Attempted to parse invalid value from storage under key "${storageKey}".`),
                {
                    extra: parsedItem,
                },
            );
            return initialValue;
        }
        return parsedItem as T;
    } catch {
        throwErrorUnlessProduction(new Error(`Failed to parse value from storage under key "${storageKey}".`));
        return initialValue;
    }
}

/** Writes a value as `useStorageState` would, outside React. */
export function setStorageState<T>(
    storageKey: string,
    value: T,
    options: SetStorageStateOptions = { storage: sessionStorage, enabled: true, validation: () => true },
): void {
    const { storage = sessionStorage, enabled = true, validation = () => true } = options;

    if (!enabled) {
        return;
    }

    if (!isProductionEnvironment() && !isSerializable(value)) {
        throwErrorUnlessProduction(new Error(`Attempted to store non-serializable value under key "${storageKey}".`), {
            extra: { value },
        });
        return;
    }

    if (value === undefined) {
        storage.removeItem(storageKey);
        return;
    }

    const serialized = JSON.stringify(value);
    storage.setItem(storageKey, serialized);

    try {
        const parsed = JSON.parse(serialized);
        if (!validation(parsed)) {
            throwErrorUnlessProduction(
                new Error(`Value failed validation after serialization for key "${storageKey}".`),
                { extra: { value, parsed } },
            );
            storage.removeItem(storageKey);
            return;
        }
    } catch (error) {
        throwErrorUnlessProduction(new Error(`Failed to validate serialized value for key "${storageKey}".`), {
            extra: { value, error },
        });
        return;
    }
}

function isSerializable(value: unknown): boolean {
    try {
        return isEqual(JSON.parse(JSON.stringify(value)), value);
    } catch {
        return false;
    }
}
